<template>
  <main class="record-page">
    <section class="filter-card">
      <div class="section-heading">
        <span>查询条件</span>
        <h2>选择地块与种植阶段</h2>
      </div>

      <label>地块名称
        <el-select v-model="landId" placeholder="请选择地块" :loading="landLoading" @change="onLandChange">
          <el-option v-for="land in lands" :key="land.id" :label="land.name" :value="land.id" />
        </el-select>
      </label>

      <label>种植阶段（可选）
        <el-select v-model="stageId" clearable placeholder="全部种植阶段" :disabled="!landId" :loading="stageLoading" @change="loadRecords(true)">
          <el-option v-for="stage in stages" :key="stage.id" :label="stage.name" :value="stage.id" />
        </el-select>
      </label>

      <p v-if="contextMissing" class="context-tip">当前环境未获取到农场信息，请从 App 进入页面。</p>
      <p v-else-if="errorMsg" class="request-error">{{ errorMsg }} <button type="button" @click="loadRecords(true)">重新加载</button></p>
    </section>

    <section v-if="landId" class="records-section">
      <div class="list-heading">
        <div>
          <span>{{ selectedLandName }}</span>
          <h2>{{ pageTitle }}</h2>
        </div>
        <small>{{ totalElements }} 条</small>
      </div>

      <div v-loading="recordLoading && !records.length" class="record-list">
        <article v-for="record in records" :key="record.id" class="record-card">
          <div class="record-header">
            <div>
              <strong>{{ recordHeading(record) }}</strong>
              <small><i class="el-icon-time" />{{ recordTime(record) }}</small>
            </div>
            <span class="stage-tag">{{ stageName(record) }}</span>
          </div>

          <div class="record-details">
            <template v-if="isPatrol">
              <span>发现位置</span><strong>{{ record.address || '-' }}</strong>
              <span>问题描述</span><strong>{{ record.description || '-' }}</strong>
            </template>
            <template v-else>
              <span>农事类型</span><strong>{{ record.type || '-' }}</strong>
              <span>作业内容</span><strong>{{ record.content || '-' }}</strong>
            </template>
          </div>

          <div v-if="recordImages(record.id).length" class="record-images">
            <el-image
              v-for="image in recordImages(record.id)"
              :key="image.id || image.url"
              :src="image.url"
              :preview-src-list="recordImageUrls(record.id)"
              fit="cover"
              lazy
            >
              <div slot="error" class="image-error"><i class="el-icon-picture-outline" /></div>
            </el-image>
          </div>
        </article>

        <el-empty v-if="!recordLoading && !records.length" description="暂无记录" :image-size="80" />
        <el-button v-if="!lastPage && records.length" class="load-more" :loading="recordLoading" @click="loadMore">加载更多</el-button>
      </div>
    </section>

    <el-empty v-else-if="!landLoading && !contextMissing" description="请先选择地块" :image-size="92" />

    <button type="button" class="add-button" aria-label="添加记录" @click="openDrawer">
      <i class="el-icon-plus" /><span>添加记录</span>
    </button>

    <el-drawer
      :title="'添加' + pageTitle"
      :visible.sync="drawerVisible"
      direction="btt"
      size="82%"
      :before-close="beforeDrawerClose"
      :close-on-press-escape="!saving"
      :wrapper-closable="!saving"
      custom-class="record-drawer"
      @closed="resetForm"
    >
      <div class="drawer-body">
        <div class="selection-summary">
          <span>{{ selectedLandName }}</span>
          <small>请选择本次记录所属阶段</small>
        </div>

        <el-form ref="recordForm" :model="form" :rules="rules" label-position="top" @submit.native.prevent>
          <el-form-item label="种植阶段" prop="stageId">
            <el-select v-model="form.stageId" placeholder="请选择种植阶段">
              <el-option v-for="stage in stages" :key="stage.id" :label="stage.name" :value="stage.id" />
            </el-select>
          </el-form-item>

          <template v-if="isPatrol">
            <el-form-item label="记录时间" prop="recordTime">
              <el-date-picker v-model="form.recordTime" type="datetime" value-format="yyyy-MM-dd HH:mm:ss" placeholder="请选择记录时间" />
            </el-form-item>
            <el-form-item label="发现位置" prop="address">
              <el-input v-model.trim="form.address" maxlength="100" show-word-limit placeholder="请输入发现位置" />
            </el-form-item>
            <el-form-item label="问题描述" prop="description">
              <el-input v-model.trim="form.description" type="textarea" :rows="4" maxlength="500" show-word-limit placeholder="请输入巡田情况" />
            </el-form-item>
          </template>

          <template v-else>
            <el-form-item label="农事类型" prop="type">
              <el-select v-model="form.type" placeholder="请选择农事类型">
                <el-option v-for="type in farmingTypes" :key="type" :label="type" :value="type" />
              </el-select>
            </el-form-item>
            <el-form-item label="作业时间" prop="time">
              <el-date-picker v-model="form.time" type="datetime" value-format="yyyy-MM-dd HH:mm:ss" placeholder="请选择作业时间" />
            </el-form-item>
            <el-form-item label="作业内容" prop="content">
              <el-input v-model.trim="form.content" type="textarea" :rows="4" maxlength="500" show-word-limit placeholder="请输入农事作业内容" />
            </el-form-item>
          </template>

          <el-form-item label="现场图片">
            <el-upload
              action="#"
              accept=".jpg,.jpeg,.png,image/jpeg,image/png"
              list-type="picture-card"
              multiple
              :auto-upload="false"
              :file-list="fileList"
              :limit="9"
              :on-change="handleFileChange"
              :on-remove="handleFileRemove"
              :on-exceed="handleFileExceed"
            >
              <i class="el-icon-plus" />
            </el-upload>
            <p class="upload-tip">最多 9 张，支持 JPG、JPEG、PNG，单张不超过 10MB</p>
          </el-form-item>
        </el-form>

        <el-button class="submit-button" type="primary" :loading="saving" @click="submitRecord">
          {{ savedRecordId ? '重试上传图片' : '保存记录' }}
        </el-button>
      </div>
    </el-drawer>
  </main>
</template>

<script>
import {
  farmingRecordFindAll,
  farmingRecordSave,
  getLand,
  getRecordImages,
  patrolRecordFindPage,
  patrolRecordSave,
  plantStageFindAll,
  uploadRecordImage
} from '../utils/getxx'

const PAGE_SIZE = 10
const MAX_IMAGE_SIZE = 10 * 1024 * 1024
const farmingTypes = ['灌溉', '施肥', '植保', '化控', '种植', '其他']

function nowString() {
  const pad = value => String(value).padStart(2, '0')
  const now = new Date()
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
}

function emptyForm() {
  const now = nowString()
  return { stageId: '', recordTime: now, address: '', description: '', type: '', time: now, content: '' }
}

export default {
  name: 'RecordList',
  props: {
    recordType: { type: String, required: true }
  },
  data() {
    return {
      lands: [], stages: [], records: [], imageMap: {}, landId: '', stageId: '', pageNum: 1,
      totalElements: 0, lastPage: true, landLoading: false, stageLoading: false, recordLoading: false,
      drawerVisible: false, saving: false, savedRecordId: '', fileList: [], form: emptyForm(), errorMsg: '', contextMissing: false,
      farmingTypes,
      rules: {
        stageId: [{ required: true, message: '请选择种植阶段', trigger: 'change' }],
        recordTime: [{ required: true, message: '请选择记录时间', trigger: 'change' }],
        address: [{ required: true, message: '请输入发现位置', trigger: 'blur' }],
        description: [{ required: true, message: '请输入问题描述', trigger: 'blur' }],
        type: [{ required: true, message: '请选择农事类型', trigger: 'change' }],
        time: [{ required: true, message: '请选择作业时间', trigger: 'change' }],
        content: [{ required: true, message: '请输入作业内容', trigger: 'blur' }]
      }
    }
  },
  computed: {
    isPatrol() { return this.recordType === 'patrol' },
    pageTitle() { return this.isPatrol ? '巡田记录' : '农事记录' },
    farmId() { return this.$route.query.farmId || localStorage.getItem('Farm-Id') || '' },
    selectedLandName() { const land = this.lands.find(item => String(item.id) === String(this.landId)); return land ? land.name : '' }
  },
  watch: {
    recordType() {
      this.drawerVisible = false
      this.records = []
      this.imageMap = {}
      if (this.landId) this.loadRecords(true)
    }
  },
  created() { this.loadLands() },
  methods: {
    listFrom(response) {
      const data = response && response.data != null ? response.data : response
      if (Array.isArray(data)) return data
      return data && Array.isArray(data.content) ? data.content : []
    },
    pageFrom(response) {
      const data = response && response.data != null ? response.data : response
      return data && typeof data === 'object' ? data : { content: [], totalElements: 0, last: true }
    },
    async loadLands() {
      if (!this.farmId) { this.contextMissing = true; return }
      this.landLoading = true
      try { this.lands = this.listFrom(await getLand({ farmId: this.farmId })) } catch (error) { this.$message.error('地块加载失败，请检查网络或登录状态') } finally { this.landLoading = false }
    },
    async onLandChange() {
      this.stageId = ''
      this.stages = []
      this.records = []
      this.imageMap = {}
      if (!this.landId) return
      await this.loadStages()
      this.loadRecords(true)
    },
    async loadStages() {
      this.stageLoading = true
      try { this.stages = this.listFrom(await plantStageFindAll({ landId: this.landId, farmId: this.farmId })) } catch (error) { this.$message.error('种植阶段加载失败') } finally { this.stageLoading = false }
    },
    async loadRecords(reset) {
      if (!this.landId || this.recordLoading) return
      if (reset) { this.pageNum = 1; this.records = []; this.imageMap = {}; this.lastPage = true }
      this.recordLoading = true
      this.errorMsg = ''
      try {
        const params = { landId: this.landId, pageSize: PAGE_SIZE, pageNum: this.pageNum }
        if (this.stageId) params.stageId = this.stageId
        const page = this.pageFrom(await (this.isPatrol ? patrolRecordFindPage(params) : farmingRecordFindAll(params)))
        const nextRecords = Array.isArray(page.content) ? page.content : []
        this.records = reset ? nextRecords : this.records.concat(nextRecords)
        this.totalElements = Number(page.totalElements || this.records.length)
        this.lastPage = page.last === true || nextRecords.length < PAGE_SIZE
        this.loadImages(nextRecords)
      } catch (error) {
        this.errorMsg = '记录加载失败，请检查网络后重试'
      } finally { this.recordLoading = false }
    },
    loadMore() { if (!this.lastPage) { this.pageNum += 1; this.loadRecords(false) } },
    async loadImages(records) {
      await Promise.all(records.map(async record => {
        try { this.$set(this.imageMap, record.id, this.listFrom(await getRecordImages({ recordId: record.id }))) } catch (error) { this.$set(this.imageMap, record.id, []) }
      }))
    },
    recordImages(recordId) { return this.imageMap[recordId] || [] },
    recordImageUrls(recordId) { return this.recordImages(recordId).map(image => image.url).filter(Boolean) },
    stageName(record) {
      if (record.stageName) return record.stageName
      const stage = this.stages.find(item => String(item.id) === String(record.stageId))
      return stage ? stage.name : '未标注阶段'
    },
    recordHeading(record) { return this.isPatrol ? (record.address || '巡田记录') : (record.type || '农事记录') },
    recordTime(record) { return (this.isPatrol ? record.recordTime : record.time) || '-' },
    openDrawer() {
      if (!this.landId) { this.$message.warning('请先选择地块'); return }
      if (!this.stages.length) { this.$message.warning('当前地块暂无可选种植阶段'); return }
      this.form = emptyForm()
      this.form.stageId = this.stageId || ''
      this.drawerVisible = true
    },
    resetForm() {
      this.$refs.recordForm && this.$refs.recordForm.clearValidate()
      this.form = emptyForm()
      this.fileList = []
      this.savedRecordId = ''
      this.saving = false
    },
    beforeDrawerClose(done) {
      if (this.saving) return
      if (!this.savedRecordId || !this.fileList.length) { done(); return }
      this.$confirm('记录已保存，仍有图片未上传，确认关闭吗？', '提示', { type: 'warning', confirmButtonText: '关闭', cancelButtonText: '继续上传' }).then(done).catch(() => {})
    },
    handleFileChange(file, files) {
      const raw = file.raw
      const validType = raw && (['image/jpeg', 'image/png'].includes(raw.type) || /\.(jpe?g|png)$/i.test(raw.name || ''))
      if (!validType || raw.size > MAX_IMAGE_SIZE) {
        this.$message.error(!validType ? '仅支持 JPG、JPEG、PNG 图片' : '单张图片不能超过 10MB')
        this.fileList = files.filter(item => item.uid !== file.uid)
        return
      }
      this.fileList = files
    },
    handleFileRemove(file, files) { this.fileList = files },
    handleFileExceed() { this.$message.warning('最多只能选择 9 张图片') },
    submitRecord() {
      if (this.saving) return
      if (this.savedRecordId) { this.uploadImages(this.savedRecordId); return }
      this.$refs.recordForm.validate(async valid => {
        if (!valid) return
        this.saving = true
        try {
          const payload = this.isPatrol
            ? { landId: this.landId, stageId: this.form.stageId, recordTime: this.form.recordTime, address: this.form.address, description: this.form.description }
            : { landId: this.landId, stageId: this.form.stageId, type: this.form.type, time: this.form.time, content: this.form.content }
          const response = await (this.isPatrol ? patrolRecordSave(payload) : farmingRecordSave(payload))
          const result = response && response.data != null ? response.data : response
          if (!result || Number(result.code) !== 200 || !result.id) throw new Error(result && result.message ? result.message : '保存失败')
          this.savedRecordId = result.id
          if (this.fileList.length) { await this.uploadImages(result.id); return }
          this.finishSave('记录保存成功')
        } catch (error) {
          this.$message.error(error.message || '记录保存失败')
        } finally { this.saving = false }
      })
    },
    async uploadImages(recordId) {
      this.saving = true
      const failed = []
      for (const file of this.fileList) {
        try {
          const data = new FormData()
          data.append('recordId', String(recordId))
          data.append('file', file.raw)
          await uploadRecordImage(data)
        } catch (error) { failed.push(file) }
      }
      this.fileList = failed
      this.saving = false
      if (failed.length) { this.$message.warning(`${failed.length} 张图片上传失败，请重试`); return }
      this.finishSave('记录和图片保存成功')
    },
    finishSave(message) {
      this.$message.success(message)
      this.drawerVisible = false
      this.loadRecords(true)
    }
  }
}
</script>

<style scoped>
.record-page { height: 100vh; min-height: 100vh; padding: 14px 14px 96px; overflow-y: auto; -webkit-overflow-scrolling: touch; box-sizing: border-box; color: #26384a; background: #f2f7fc; text-align: left; }
.filter-card, .record-card { border: 1px solid #e5eef7; border-radius: 14px; background: #fff; box-shadow: 0 5px 16px rgba(70, 119, 159, .08); }
.filter-card { padding: 17px; }.section-heading { margin-bottom: 16px; padding-left: 10px; border-left: 3px solid #5ba1e6; }.section-heading span, .list-heading span { color: #5ba1e6; font-size: 11px; font-weight: 700; letter-spacing: 1px; }.section-heading h2, .list-heading h2 { margin: 3px 0 0; color: #26384a; font-size: 18px; }.filter-card label { display: block; margin-top: 15px; color: #526578; font-size: 13px; font-weight: 600; }.filter-card .el-select { display: block; width: 100%; margin-top: 8px; }.context-tip, .request-error { margin: 12px 0 0; font-size: 12px; line-height: 1.5; }.context-tip { color: #8096ad; }.request-error { color: #d84a3f; }.request-error button { padding: 0; border: 0; color: #347fc5; background: transparent; font: inherit; }
.records-section { margin-top: 18px; }.list-heading { display: flex; align-items: flex-end; justify-content: space-between; padding: 0 4px 2px; }.list-heading small { color: #8096ad; font-size: 12px; }.record-list { min-height: 100px; }.record-card { margin-top: 12px; padding: 15px; }.record-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; padding-bottom: 11px; border-bottom: 1px solid #e4f0fb; }.record-header strong { display: block; color: #2c4054; font-size: 16px; }.record-header small { display: block; margin-top: 5px; color: #8096ad; font-size: 12px; }.record-header small i { margin-right: 5px; }.stage-tag { flex: 0 0 auto; max-width: 42%; overflow: hidden; padding: 4px 9px; color: #347fc5; border-radius: 999px; background: #edf6ff; font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }.record-details { display: grid; grid-template-columns: 72px minmax(0, 1fr); gap: 9px; margin-top: 12px; font-size: 13px; line-height: 1.55; }.record-details span { color: #8096ad; }.record-details strong { color: #40576d; font-weight: 500; word-break: break-word; }.record-images { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-top: 13px; }.record-images .el-image { width: 100%; aspect-ratio: 1; overflow: hidden; border-radius: 9px; background: #edf6ff; }.image-error { display: grid; width: 100%; height: 100%; place-items: center; color: #8096ad; }.load-more { display: block; width: 100%; height: 42px; margin-top: 12px; border-color: #dbe8f4; color: #5b7791; background: #fff; }
.add-button { position: fixed; z-index: 4; right: 18px; bottom: calc(18px + env(safe-area-inset-bottom)); display: inline-flex; align-items: center; gap: 7px; min-height: 48px; padding: 0 18px; border: 0; border-radius: 24px; color: #fff; background: #5ba1e6; box-shadow: 0 8px 18px rgba(52, 127, 197, .3); font-size: 15px; font-weight: 600; }.add-button i { font-size: 17px; }
.drawer-body { height: 100%; padding: 0 18px calc(18px + env(safe-area-inset-bottom)); overflow-y: auto; box-sizing: border-box; }.selection-summary { margin-bottom: 16px; padding: 11px 12px; border-radius: 9px; background: #edf6ff; }.selection-summary span, .selection-summary small { display: block; }.selection-summary span { color: #347fc5; font-weight: 600; }.selection-summary small { margin-top: 4px; color: #8096ad; font-size: 12px; }.drawer-body .el-select, .drawer-body .el-date-editor { width: 100%; }.upload-tip { margin: 6px 0 0; color: #8096ad; font-size: 11px; line-height: 1.45; }.submit-button { width: 100%; min-height: 44px; margin-bottom: 4px; border-color: #5ba1e6; background: #5ba1e6; font-size: 16px; font-weight: 600; }
::v-deep .el-input__inner { height: 42px; border-color: #dbe6f0; color: #40556a; border-radius: 8px; }::v-deep .el-input__inner:focus, ::v-deep .el-textarea__inner:focus { border-color: #5ba1e6; box-shadow: 0 0 0 2px rgba(91, 161, 230, .12); }::v-deep .record-drawer { border-radius: 18px 18px 0 0; }::v-deep .record-drawer .el-drawer__header { margin-bottom: 12px; padding: 18px 20px 12px; color: #26384a; font-size: 18px; font-weight: 600; }::v-deep .record-drawer .el-drawer__body { min-height: 0; }::v-deep .drawer-body .el-form-item { margin-bottom: 17px; }::v-deep .drawer-body .el-form-item__label { padding-bottom: 7px; color: #526578; font-weight: 600; line-height: 1.3; }::v-deep .drawer-body .el-textarea__inner { border-color: #dbe6f0; border-radius: 8px; font-family: inherit; }::v-deep .drawer-body .el-upload--picture-card, ::v-deep .drawer-body .el-upload-list--picture-card .el-upload-list__item { width: 76px; height: 76px; line-height: 76px; border-radius: 9px; }::v-deep .drawer-body .el-upload--picture-card { border-color: #bfd5e8; background: #f7fbff; }::v-deep .el-button--primary { border-color: #5ba1e6; background: #5ba1e6; }
@media (min-width: 700px) { .record-page { padding-right: max(16px, calc((100% - 680px) / 2)); padding-left: max(16px, calc((100% - 680px) / 2)); }.record-images { grid-template-columns: repeat(6, 1fr); } }
</style>
